import { balaDepreciationPct } from '../data/balaTable';
import { ProjectSummary } from '../services/api';

// ── Model ───────────────────────────────────────────────────────────────────
// Benchmark direction: resale PSF is adjusted UP to its "brand-new equivalent"
// so it can be compared head-to-head with new-launch asking PSF.
//
//   personalized PSF = avg project PSF ÷ (1 − tenure depreciation)  [Level 2]
//                    + physical & functional restoration            [Level 3]
//                    + amenity premiums beyond preferred distance   [Level 4]

export type Level = 1 | 2 | 3 | 4;
export type TenureChoice = 'leasehold' | 'freehold';
export type LeaseholdMethod = 'linear' | 'bala';
export type FreeholdMethod = 'landBuilding' | 'marketDiscount';
export type AdjustMode = 'pct' | 'psf';

/** Piecewise-linear curve: building age (years) → remaining value % vs brand-new (100 = new). */
export interface CurvePoint { age: number; pct: number; }

export interface AgeBandAdjust {
  id: string;
  fromAge: number;
  toAge: number;
  value: number;
  mode: AdjustMode;
}

export interface ExtraAdjust {
  id: string;
  label: string;
  value: number;
  mode: AdjustMode;
  enabled: boolean;
}

/**
 * Level 4: the buyer's preferred distance to an amenity. A project farther
 * away than this is a weaker location, so the premium is added to lift its
 * PSF to what a well-located equivalent would cost.
 */
export interface AmenityPremium {
  enabled: boolean;
  preferredDistanceMeters: number;
  value: number;
  mode: AdjustMode;
}

export interface AdjustmentState {
  level: Level;
  tenureChoice: TenureChoice;
  leaseholdMethod: LeaseholdMethod;
  freeholdMethod: FreeholdMethod;
  /** True once the user drags a point / edits the table — curve becomes "Custom". */
  curveEdited: boolean;
  curve: CurvePoint[];
  // Linear (leasehold) parameter — straight line to ZERO value at this age,
  // i.e. the original lease term (99-year lease → worthless at age 99).
  linearTermYears: number;
  // Land + building split (freehold) parameters
  landSharePct: number;       // land % of total value (land never depreciates)
  buildingLifeYears: number;  // economic life of the building component
  // Market-derived discount (freehold) parameters
  marketRatePct: number;      // observed age discount % per year
  marketCapPct: number;       // cap on total age discount
  // Level 3
  ageBands: AgeBandAdjust[];
  extras: ExtraAdjust[];
  // Level 4 — premium for projects beyond the preferred amenity distance
  mrtPremium: AmenityPremium;
  priSchPremium: AmenityPremium;
}

export const CURVE_AGES = [0, 5, 10, 15, 20, 25, 30, 40, 50, 60, 80, 99] as const;
export const MAX_CURVE_PCT = 100;

export const DEFAULT_MRT_PREFERRED_METERS = 500;
export const DEFAULT_PRI_SCH_PREFERRED_METERS = 1000;

function defaultAmenityPremium(preferredDistanceMeters: number): AmenityPremium {
  return { enabled: true, preferredDistanceMeters, value: 0, mode: 'pct' };
}

function normalizeAmenityPremium(value: unknown, defaults: AmenityPremium): AmenityPremium {
  if (!value || typeof value !== 'object') return defaults;
  const source = value as Partial<AmenityPremium>;
  const distance = Number(source.preferredDistanceMeters);
  const amount = Number(source.value);
  return {
    enabled: source.enabled !== false,
    preferredDistanceMeters: Number.isFinite(distance) && distance >= 0 ? distance : defaults.preferredDistanceMeters,
    value: Number.isFinite(amount) ? amount : 0,
    mode: source.mode === 'psf' ? 'psf' : 'pct',
  };
}

/** True when the project sits beyond the preferred distance (unknown distance never qualifies). */
export function amenityPremiumApplies(premium: AmenityPremium | undefined, distance: number | null): boolean {
  return Boolean(premium?.enabled)
    && distance != null && Number.isFinite(distance) && distance >= 0
    && distance > premium!.preferredDistanceMeters;
}

/** $psf the premium adds to a project at `distance`; 0 when it does not apply. */
export function amenityPremiumAmount(premium: AmenityPremium | undefined, distance: number | null, basePsf: number): number {
  if (!amenityPremiumApplies(premium, distance)) return 0;
  return Math.round(premium!.mode === 'pct' ? basePsf * premium!.value / 100 : premium!.value);
}

// ── Curve seeding ───────────────────────────────────────────────────────────

/** Straight line to zero remaining value at the end of the lease term. */
export function seedLinearCurve(termYears: number): CurvePoint[] {
  const term = Math.max(1, termYears);
  return CURVE_AGES.map((age) => ({
    age,
    pct: round1(Math.max(0, (1 - age / term) * 100)),
  }));
}

export function seedBalaCurve(): CurvePoint[] {
  return CURVE_AGES.map((age) => ({
    age,
    pct: round1(Math.max(0, 100 - balaDepreciationPct(age))),
  }));
}

/**
 * Freehold, land + building split (cost approach): only the building
 * component depreciates, straight-line over its economic life.
 *   remaining(age) = 100 − buildingShare × min(age, life) / life
 */
export function seedLandBuildingCurve(landSharePct: number, buildingLifeYears: number): CurvePoint[] {
  const buildingShare = Math.max(0, Math.min(100, 100 - landSharePct));
  const life = Math.max(1, buildingLifeYears);
  return CURVE_AGES.map((age) => ({
    age,
    pct: round1(Math.max(0, 100 - Math.min(MAX_CURVE_PCT, buildingShare * Math.min(age, life) / life))),
  }));
}

/** Freehold, market-derived age discount: flat %/year capped at marketCapPct. */
export function seedMarketDiscountCurve(ratePct: number, capPct: number): CurvePoint[] {
  return CURVE_AGES.map((age) => ({
    age,
    pct: round1(Math.max(0, 100 - Math.min(capPct, Math.min(MAX_CURVE_PCT, age * ratePct)))),
  }));
}

export function seedCurve(state: AdjustmentState): CurvePoint[] {
  if (state.tenureChoice === 'leasehold') {
    return state.leaseholdMethod === 'bala'
      ? seedBalaCurve()
      : seedLinearCurve(state.linearTermYears);
  }
  return state.freeholdMethod === 'landBuilding'
    ? seedLandBuildingCurve(state.landSharePct, state.buildingLifeYears)
    : seedMarketDiscountCurve(state.marketRatePct, state.marketCapPct);
}

export function defaultAdjustmentState(tenureChoice: TenureChoice): AdjustmentState {
  const base: AdjustmentState = {
    level: 1,
    tenureChoice,
    leaseholdMethod: 'linear',
    freeholdMethod: 'landBuilding',
    curveEdited: false,
    curve: [],
    linearTermYears: 99, // standard 99-year lease — value reaches $0 at age 99
    landSharePct: 70,
    buildingLifeYears: 50,
    marketRatePct: 0.5,
    marketCapPct: 30,
    ageBands: [
      { id: 'band-1', fromAge: 0, toAge: 5, value: 0, mode: 'pct' },
      { id: 'band-2', fromAge: 5, toAge: 10, value: 3, mode: 'pct' },
      { id: 'band-3', fromAge: 10, toAge: 20, value: 6, mode: 'pct' },
      { id: 'band-4', fromAge: 20, toAge: 30, value: 9, mode: 'pct' },
      { id: 'band-5', fromAge: 30, toAge: 99, value: 12, mode: 'pct' },
    ],
    extras: [
      { id: 'extra-newsale', label: 'New-launch premium', value: 5, mode: 'pct', enabled: false },
      { id: 'extra-reno', label: 'Renovation / condition', value: 0, mode: 'pct', enabled: false },
      { id: 'extra-facilities', label: 'Facilities & layout obsolescence', value: 0, mode: 'pct', enabled: false },
    ],
    mrtPremium: defaultAmenityPremium(DEFAULT_MRT_PREFERRED_METERS),
    priSchPremium: defaultAmenityPremium(DEFAULT_PRI_SCH_PREFERRED_METERS),
  };
  base.curve = seedCurve(base);
  return base;
}

/**
 * Adds the Level 4 fields to states saved before Amenities existed.  Keeping
 * this at the model boundary means history/restore and future persistence
 * callers can safely pass partial legacy objects through the calculator.
 */
export function normalizeAdjustmentState(
  raw: Partial<AdjustmentState> | null | undefined,
  tenureChoice: TenureChoice = 'leasehold',
): AdjustmentState {
  const defaults = defaultAdjustmentState(raw?.tenureChoice || tenureChoice);
  return {
    ...defaults,
    ...raw,
    level: raw?.level === 4 ? 4 : (raw?.level === 3 ? 3 : (raw?.level === 2 ? 2 : 1)),
    mrtPremium: normalizeAmenityPremium(raw?.mrtPremium, defaults.mrtPremium),
    priSchPremium: normalizeAmenityPremium(raw?.priSchPremium, defaults.priSchPremium),
  };
}

// ── Curve evaluation ────────────────────────────────────────────────────────

/** Linear interpolation on the sorted curve; flat extrapolation past the ends. */
export function curvePctAtAge(curve: CurvePoint[], age: number): number {
  if (curve.length === 0) return 0;
  const pts = [...curve].sort((a, b) => a.age - b.age);
  if (age <= pts[0].age) return pts[0].pct;
  const last = pts[pts.length - 1];
  if (age >= last.age) return last.pct;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = pts[i];
    const b = pts[i + 1];
    if (age >= a.age && age <= b.age) {
      const t = b.age === a.age ? 0 : (age - a.age) / (b.age - a.age);
      return a.pct + t * (b.pct - a.pct);
    }
  }
  return last.pct;
}

// ── Project age / lease helpers ─────────────────────────────────────────────

/** Approximate years between lease commencement and completion (TOP). */
const CONSTRUCTION_LAG_YEARS = 3;

export interface ProjectAgeInfo {
  age: number | null;
  estimated: boolean;
  tenureYears: number | null;       // null for Freehold
  remainingLease: number | null;    // null for Freehold / unknown
}

export function projectAgeInfo(p: ProjectSummary, nowYear: number): ProjectAgeInfo {
  const tenureRaw = (p.tenure || '').trim();
  const tenureYears = /^\d+$/.test(tenureRaw) ? parseInt(tenureRaw, 10) : null;
  const leaseStartYear = p.tenureStartDate
    ? new Date(p.tenureStartDate).getFullYear()
    : (p.completionYear ? p.completionYear - CONSTRUCTION_LAG_YEARS : null);

  let age: number | null = null;
  let estimated = false;
  if (p.completionYear) {
    age = Math.max(0, nowYear - p.completionYear);
  } else if (leaseStartYear) {
    age = Math.max(0, nowYear - leaseStartYear - CONSTRUCTION_LAG_YEARS);
    estimated = true;
  }

  let remainingLease: number | null = null;
  if (tenureYears != null && leaseStartYear != null) {
    remainingLease = Math.max(0, tenureYears - (nowYear - leaseStartYear));
  }

  return { age, estimated, tenureYears, remainingLease };
}

// ── Personalized PSF computation ────────────────────────────────────────────

export interface PsfBreakdown {
  basePsf: number | null;
  tenureAdj: number;       // Level 2 $psf added
  physicalAdj: number;     // Level 3 $psf added
  amenitiesAdj: number;    // Level 4 $psf added
  personalizedPsf: number | null;
  depPct: number | null;   // tenure depreciation % used
  ageUsed: number | null;
  ageEstimated: boolean;
  // Level 3 detail for the derivation display
  band: AgeBandAdjust | null;
  bandAmount: number;      // $psf contributed by the matched age band
  extrasApplied: Array<{ label: string; amount: number; value?: number; mode?: AdjustMode }>;
  /** The premium setting when the project is beyond the preferred distance, else null. */
  mrtPremium: AmenityPremium | null;
  mrtPremiumAmount: number;
  priSchPremium: AmenityPremium | null;
  priSchPremiumAmount: number;
}

/** Defensive numeric coercion — DECIMAL columns can arrive as strings. */
function toNum(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function computePersonalizedPsf(
  p: ProjectSummary,
  state: AdjustmentState,
  nowYear: number,
): PsfBreakdown {
  const basePsf = toNum(p.avgPsf);
  const empty: PsfBreakdown = {
    basePsf, tenureAdj: 0, physicalAdj: 0, amenitiesAdj: 0,
    personalizedPsf: basePsf, depPct: null, ageUsed: null, ageEstimated: false,
    band: null, bandAmount: 0, extrasApplied: [], mrtPremium: null, mrtPremiumAmount: 0,
    priSchPremium: null, priSchPremiumAmount: 0,
  };
  if (basePsf == null) return empty;
  if (state.level === 1) return empty;

  const info = projectAgeInfo(p, nowYear);
  let depPct: number | null = null;
  let tenureAdj = 0;
  let personalized = basePsf;

  // Projects without age data retain the raw PSF, but Level 4 amenities can
  // still be applied when distance data is available.
  if (info.age != null) {
    // Curve stores remaining value % vs new; convert to depreciation for the restore formula.
    const remainingPct = Math.max(5, Math.min(100, curvePctAtAge(state.curve, info.age)));
    depPct = Math.max(0, Math.min(95, 100 - remainingPct));
    const restored = basePsf / (1 - depPct / 100);
    tenureAdj = Math.round(restored - basePsf);
    personalized += tenureAdj;
  }

  let physicalAdj = 0;
  let band: AgeBandAdjust | null = null;
  let bandAmount = 0;
  const extrasApplied: Array<{ label: string; amount: number; value: number; mode: AdjustMode }> = [];
  if (state.level >= 3) {
    if (info.age != null) {
      band = state.ageBands.find((b) => info.age! >= b.fromAge && info.age! < b.toAge)
        || state.ageBands.find((b) => info.age! >= b.fromAge && info.age! <= b.toAge)
        || null;
      if (band) {
        bandAmount = Math.round(band.mode === 'pct' ? basePsf * band.value / 100 : band.value);
        physicalAdj += bandAmount;
      }
      for (const extra of state.extras) {
        if (!extra.enabled) continue;
        const amount = Math.round(extra.mode === 'pct' ? basePsf * extra.value / 100 : extra.value);
        extrasApplied.push({ label: extra.label, amount, value: extra.value, mode: extra.mode });
        physicalAdj += amount;
      }
      personalized += physicalAdj;
    }
  }

  let amenitiesAdj = 0;
  let mrtPremium: AmenityPremium | null = null;
  let mrtPremiumAmount = 0;
  let priSchPremium: AmenityPremium | null = null;
  let priSchPremiumAmount = 0;
  if (state.level === 4) {
    const mrtDistance = toNum(p.nearestMrtDistance);
    const priSchDistance = toNum(p.nearestPriSchDistance);
    if (amenityPremiumApplies(state.mrtPremium, mrtDistance)) {
      mrtPremium = state.mrtPremium;
      mrtPremiumAmount = amenityPremiumAmount(state.mrtPremium, mrtDistance, basePsf);
    }
    if (amenityPremiumApplies(state.priSchPremium, priSchDistance)) {
      priSchPremium = state.priSchPremium;
      priSchPremiumAmount = amenityPremiumAmount(state.priSchPremium, priSchDistance, basePsf);
    }
    amenitiesAdj = mrtPremiumAmount + priSchPremiumAmount;
    personalized += amenitiesAdj;
  }

  return {
    basePsf,
    tenureAdj,
    physicalAdj,
    amenitiesAdj,
    personalizedPsf: Math.round(personalized),
    depPct: depPct == null ? null : round1(depPct),
    ageUsed: info.age,
    ageEstimated: info.estimated,
    band,
    bandAmount,
    extrasApplied,
    mrtPremium,
    mrtPremiumAmount,
    priSchPremium,
    priSchPremiumAmount,
  };
}

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
